import { pageTitle } from '@palaccount/config';
import { PRINT_SETTING_KEYS, type SettingRow, type SettingValue } from '@palaccount/contracts';
import type { RenderInput, SalesInvoiceData } from '@palaccount/documents';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { buildPrintHtml, printOptionsFromSettings } from '@/lib/print-document';
import { useBaseCurrency } from '@/lib/session';
import { localizeServerMessage } from '@/lib/server-messages';
import { useMe } from '@/lib/session';

import { useSettings, useUpdateSetting } from './api';

/**
 * INV-H6 "custom print data" / UB-03 "print options" (bug triage, Oct 2026)
 * — the Printing screen: the eleven `print.*` company settings as one form,
 * beside a live preview.
 *
 * ── Why a screen of its own when ADM-01 already draws these rows ───────────
 * ADM-01's rail lists them under "Printing" like any other group, and that is
 * where an administrator searching for "footer" will find them. But a print
 * option is the one kind of setting whose effect cannot be judged from its
 * name — "Header text" is a guess until you see where it lands — so this
 * screen renders a SAMPLE INVOICE through the same `buildPrintHtml` the Print
 * button uses, with the unsaved draft applied, and redraws it on every
 * keystroke. The preview is the paper; nothing here is a second template.
 *
 * ── Saving ─────────────────────────────────────────────────────────────────
 * The same per-group batch ADM-01 uses (the authorised BR-ADM-01-13
 * deviation, argued on `settings-page.tsx`): edits accumulate in a draft and
 * one Save writes each dirty key through the org route. Every print row is
 * `org` scope, so there is no personal-override arm here.
 *
 * ── Who may change what ────────────────────────────────────────────────────
 * The route sits under the Settings group's `admin` gate. Whether the CALLER
 * may write is the server's `canChange` on each row (BR-ADM-01-04, the same
 * answer ADM-01 reads), so an Accountant with admin View sees the options
 * dimmed with their values visible (BR-ADM-01-07) and a sentence saying who
 * can change them, rather than a Save that fails.
 */

const TEXT_KEYS = ['print.header_text', 'print.footer_text'] as const;
const SWITCH_KEYS = [
  'print.show_logo',
  'print.show_item_code',
  'print.show_discount_column',
  'print.show_tax_column',
  'print.show_warehouse',
  'print.show_bank_details',
] as const;
const PAGE_KEYS = ['print.paper', 'print.language'] as const;

const FIELD_CLASS = 'grid gap-1';
const LABEL_CLASS = 'text-sm font-medium';
const HINT_CLASS = 'text-xs text-muted-foreground';

/**
 * The invoice the preview draws. Figures are fixed so the preview is the same
 * on every machine; the names are dictionary strings so an Arabic reader sees
 * an Arabic sample. Exported for the screen test, which asserts the preview
 * carries this document and not some other.
 */
export function sampleInvoice(t: TFunction, currency: string): SalesInvoiceData {
  return {
    code: 'INV-000123',
    status: 'posted',
    invoiceDate: '2026-07-15',
    dueDate: '2026-08-14',
    partnerCode: 'CUS-0001',
    partnerName: t('printset.sampleCustomer'),
    // The tenant's own base (GEN-107), never a literal — the preview must
    // show the figures the way this company prints them.
    currency,
    // So the `print.show_warehouse` switch has something to show.
    warehouseName: 'WH-MAIN',
    lines: [
      {
        lineNo: 1,
        itemCode: 'ITM-1001',
        description: t('printset.sampleItem'),
        qty: '12',
        unit: { symbol: 'pc', precision: 0 },
        unitPrice: '45.00',
        discountPct: '5',
        lineTotal: '513.00',
      },
      {
        lineNo: 2,
        itemCode: null,
        description: t('printset.sampleService'),
        qty: '1',
        unit: null,
        unitPrice: '25.00',
        discountPct: '0',
        lineTotal: '25.00',
      },
    ],
    subtotal: '538.00',
    discountPct: '0',
    discountAmount: '0.00',
    taxPct: '15',
    taxAmount: '80.70',
    grandTotal: '618.70',
    amountPaid: '0.00',
    balanceDue: '618.70',
    notes: null,
  };
}

export function PrintSettingsPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { data: allRows, isPending, error } = useSettings();
  const updateSetting = useUpdateSetting();

  /** Unsaved edits, key → value — ADM-01's draft, for one group. */
  const [draft, setDraft] = useState<Record<string, SettingValue>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('printset.title'));
  }, [t]);

  const rows = useMemo(() => {
    const byKey = new Map<string, SettingRow>();
    for (const row of allRows ?? []) {
      if ((PRINT_SETTING_KEYS as readonly string[]).includes(row.key)) byKey.set(row.key, row);
    }
    return byKey;
  }, [allRows]);

  const current = (key: string): SettingValue | undefined => draft[key] ?? rows.get(key)?.value;
  const editable = rows.size > 0 && [...rows.values()].every((row) => row.canChange);
  const dirty = Object.keys(draft).filter((key) => {
    const row = rows.get(key);
    return row !== undefined && draft[key] !== row.value;
  });

  const setValue = (key: string, value: SettingValue) => {
    setSaved(false);
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaveError(null);
    try {
      for (const key of dirty) {
        await updateSetting.mutateAsync({ key, value: draft[key]! });
      }
      setDraft({});
      setSaved(true);
    } catch (cause) {
      setSaveError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));
    }
  };

  /*
   * THE PREVIEW — the draft laid over the saved values, through the SAME
   * builder the Print button calls. `printOptionsFromSettings` reads the
   * effective map's shape, so the saved rows are folded into that shape
   * first; a key the server has not sent yet falls to its registry default,
   * which is also what the Print button would do.
   */
  const baseCurrency = useBaseCurrency();
  const previewHtml = useMemo(() => {
    const effective: Record<string, SettingValue> = {};
    for (const [key, row] of rows) effective[key] = row.value;
    for (const [key, value] of Object.entries(draft)) effective[key] = value;
    const input: RenderInput = {
      locale: i18n.language.startsWith('ar') ? 'ar' : 'en',
      company: { name: me?.tenant.name ?? '' },
      generatedAt: '2026-07-15T09:00:00.000Z',
      generatedBy: me?.user.name ?? null,
      payload: { kind: 'sales_invoice', data: sampleInvoice(t, baseCurrency) },
    };
    return buildPrintHtml(input, printOptionsFromSettings(effective));
  }, [rows, draft, i18n.language, me, t, baseCurrency]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {localizeServerMessage(t, error.message)}
      </p>
    );
  }

  const textField = (key: (typeof TEXT_KEYS)[number] | 'print.bank_details') => (
    <div key={key} className={FIELD_CLASS}>
      <label htmlFor={key} className={LABEL_CLASS}>
        {t(`settings.label.${key}`)}
      </label>
      <p className={HINT_CLASS}>{t(`settings.hint.${key}`)}</p>
      <Textarea
        id={key}
        rows={3}
        maxLength={500}
        value={String(current(key) ?? '')}
        disabled={!editable}
        onChange={(event) => setValue(key, event.target.value)}
      />
    </div>
  );

  return (
    <div className="grid gap-4">
      <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={t('shell.navSettings')}>
        <Link
          to="/settings"
          className="rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t('settings.tabGeneral')}
        </Link>
        <span className="rounded-t-md border-b-2 border-primary px-3 py-2 text-sm font-medium">
          {t('printset.title')}
        </span>
      </nav>

      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="me-auto text-lg font-bold">{t('printset.title')}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('printset.intro')}</p>

      {isPending ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> : null}
      {!isPending && !editable && rows.size > 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('printset.readOnly')}
        </p>
      ) : null}
      {saveError ? (
        <p role="alert" className="text-sm text-destructive">
          {saveError}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid gap-4 self-start">
          <Card>
            <CardContent className="grid gap-4 py-4">
              <h3 className="text-sm font-semibold">{t('printset.sectionText')}</h3>
              {TEXT_KEYS.map((key) => textField(key))}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-3 py-4">
              <h3 className="text-sm font-semibold">{t('printset.sectionColumns')}</h3>
              {SWITCH_KEYS.map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <Checkbox
                    id={key}
                    className="mt-0.5"
                    checked={current(key) === true}
                    disabled={!editable}
                    onCheckedChange={(checked) => setValue(key, checked === true)}
                  />
                  <div className="grid gap-0.5">
                    <label htmlFor={key} className={LABEL_CLASS}>
                      {t(`settings.label.${key}`)}
                    </label>
                    <p className={HINT_CLASS}>{t(`settings.hint.${key}`)}</p>
                  </div>
                </div>
              ))}
              {textField('print.bank_details')}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="grid gap-4 py-4">
              <h3 className="text-sm font-semibold">{t('printset.sectionPage')}</h3>
              {PAGE_KEYS.map((key) => (
                <div key={key} className={FIELD_CLASS}>
                  <label htmlFor={key} className={LABEL_CLASS}>
                    {t(`settings.label.${key}`)}
                  </label>
                  <p className={HINT_CLASS}>{t(`settings.hint.${key}`)}</p>
                  <NativeSelect
                    id={key}
                    className="w-full sm:w-64"
                    value={String(current(key) ?? '')}
                    disabled={!editable}
                    onChange={(event) => setValue(key, event.target.value)}
                  >
                    {(rows.get(key)?.options ?? []).map((option) => (
                      <option key={option} value={option}>
                        {t(`settings.opt.${key}.${option}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              ))}
            </CardContent>
          </Card>

          {dirty.length > 0 ? (
            <div className="flex items-center gap-3">
              <Button onClick={() => void save()} disabled={updateSetting.isPending}>
                {t('settings.saveGroup')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDraft({});
                  setSaveError(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('settings.unsaved', { count: dirty.length })}
              </span>
            </div>
          ) : saved ? (
            <p className="text-sm text-green-700 dark:text-green-400">{t('settings.saved')}</p>
          ) : null}
        </div>

        <Card className="self-start">
          <CardContent className="grid gap-2 py-4">
            <h3 className="text-sm font-semibold">{t('printset.preview')}</h3>
            <p className={HINT_CLASS}>{t('printset.previewHint')}</p>
            {/*
             * The preview frame is FULLY sandboxed (`sandbox=""`): nothing in
             * it runs and it has an opaque origin. `print-document.ts` needs
             * `allow-same-origin` to reach `contentWindow.print()`; a preview
             * calls nothing, so it grants nothing — the same tenant-data
             * markup, with one fewer permission than the printing frame.
             */}
            <iframe
              title={t('printset.previewFrame')}
              sandbox=""
              srcDoc={previewHtml}
              className="h-[640px] w-full rounded-md border bg-white"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
